/** biome-ignore-all lint/correctness/useHookAtTopLevel: <HOC> */
'use client';

import type { ComponentType } from 'react';
import { useLayoutEffect } from 'react';
import { create } from 'zustand';

const useBottomBarVisibleStore = create<{
  hideBottomBar: () => void;
  showBottomBar: () => void;
  visible: boolean;
}>((set) => ({
  hideBottomBar: () => {
    set({ visible: false });
  },
  showBottomBar: () => {
    set({ visible: true });
  },
  visible: true,
}));

/**
 * 하단 바의 현재 가시성 상태를 가져오는 커스텀 훅
 * @returns {boolean} 하단 바의 가시성 상태
 */
export const useIsBottomBarVisible = () => useBottomBarVisibleStore((state) => state.visible);

/**
 * 하단 바를 숨기는 함수를 가져오는 커스텀 훅
 * @returns {() => void} 하단 바를 숨기는 함수
 */
export const useHideBottomBar = () => useBottomBarVisibleStore((state) => state.hideBottomBar);

/**
 * 하단 바를 보여주는 함수를 가져오는 커스텀 훅
 * @returns {() => void} 하단 바를 보여주는 함수
 */
export const useShowBottomBar = () => useBottomBarVisibleStore((state) => state.showBottomBar);

/**
 * 컴포넌트가 마운트될 때 하단 바를 숨기고, 언마운트될 때 다시 보여주는 고차 컴포넌트(HOC)
 * 클라이언트 페이지에 사용하면 해당 페이지에서는 바텀바가 없는 상태로 화면이 보여집니다.
 * @template P - 래핑된 컴포넌트의 props 타입
 * @param {ComponentType<P>} Component - 래핑할 컴포넌트
 * @returns {(props: P) => JSX.Element} 래핑된 컴포넌트
 */
export const HideBottomBarHoc = <P extends object>(Component: ComponentType<P>) => {
  return function HOC(props: P) {
    const hideBottomBar = useBottomBarVisibleStore((store) => store.hideBottomBar);
    const showBottomBar = useBottomBarVisibleStore((store) => store.showBottomBar);

    useLayoutEffect(() => {
      hideBottomBar();
      return () => {
        showBottomBar();
      };
    }, []);

    return <Component {...props} />;
  };
};
